import React, { useCallback, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Loading from "../../../common/Loading";
import Button from "components/CustomButtons/Button";
import { Box, MenuItem } from "@material-ui/core";
import {
  FINANCIAL_ACCOUNT_TYPE,
  FINANCIAL_ACCOUNT_TYPE_ID
} from "../../../../constants";
import { Close } from "@material-ui/icons";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Bank Name is required"),
  address: Yup.string().required("Address is required"),
  aba: Yup.string().required("ABA Routing Number or Swift Code is required"),
  signers: Yup.string().required("Authorized Signers are required"),
  accountTypes: Yup.array()
    .of(
      Yup.object().shape({
        financialAccountTypeID: Yup.string().required("This field is required"),
        accountno: Yup.string().required("This field is required"),
        balance: Yup.number()
      })
    )
    .min(1, "You need at least 1 account type")
});

const defaultValues = {
  name: "",
  address: "",
  aba: "",
  signers: "",
  accountTypes: []
};

const useStyles = makeStyles(theme => ({
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
}));

const CashDialog = ({ open, data, onClose, onCreate, onUpdate }) => {
  const classes = useStyles();

  const { items, isCreating } = data;

  const [isDirty, setDirty] = useState(false);

  const initialValues =
    items && items.length
      ? {
          name: items[0].name,
          address: items[0].address,
          aba: items[0].aba,
          signers: items[0].signers,
          accountTypes: items.map(i => ({
            id: i.id,
            financialAccountTypeID: i.financialAccountTypeID,
            accountno: i.accountno,
            balance: i.balance
          }))
        }
      : defaultValues;

  const handleSubmit = useCallback(
    values => {
      setDirty(true);
      if (isCreating) {
        onCreate(values);
      } else {
        const addedItems = values.accountTypes
          .filter(item => Boolean(item.id) === false)
          .map(item => ({
            name: values.name,
            address: values.address,
            aba: values.aba,
            signers: values.signers,
            financialAccountTypeID: item.financialAccountTypeID,
            accountno: item.accountno,
            balance: item.balance
          }));
        const updatedItems = values.accountTypes
          .filter(item =>
            Boolean(items.find(newItem => newItem.id === item.id))
          )
          .map(item => ({
            id: item.id,
            name: values.name,
            address: values.address,
            aba: values.aba,
            signers: values.signers,
            financialAccountTypeID: item.financialAccountTypeID,
            accountno: item.accountno,
            balance: item.balance
          }));
        const removedItems = items.filter(
          item =>
            Boolean(
              values.accountTypes.find(newItem => newItem.id === item.id)
            ) === false
        );
        onUpdate({ addedItems, updatedItems, removedItems });
      }
    },
    [isCreating]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chapter Financial Information</DialogTitle>
      <DialogContent>
        <Typography>
          <strong>Financial Institution</strong>
        </Typography>
        <Box mb={3} />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            touched,
            errors,
            setFieldValue,
            handleChange,
            handleBlur,
            isValid
          }) => (
            <Form autoComplete="off">
              <Grid container spacing={2} direction="column">
                <Grid item>
                  <TextField
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    label="Name"
                    variant="outlined"
                    name="name"
                    fullWidth
                    value={values.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address}
                    label="Address"
                    variant="outlined"
                    name="address"
                    fullWidth
                    value={values.address}
                    onChange={handleChange}
                    multiline
                    rows={5}
                  />
                </Grid>
                <Grid item>
                  <Typography>
                    <strong>Accounts</strong>
                  </Typography>
                  <Typography>
                    Please list all accounts held at this institution
                  </Typography>
                  <Box mb={1} />
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <Typography>Account type</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>Account number</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>
                        Reconciled balance as of the End of the Current Fiscal
                        Year
                      </Typography>
                    </Grid>
                  </Grid>
                  {values.accountTypes.map((type, index) => (
                    <Grid container spacing={2} key={`account-type-${index}`}>
                      <Grid item xs={5}>
                        <TextField
                          select
                          error={Boolean(
                            errors.accountTypes &&
                              errors.accountTypes[index] &&
                              errors.accountTypes[index].financialAccountTypeID
                          )}
                          helperText={
                            touched.accountTypes &&
                            touched.accountTypes[index] &&
                            touched.accountTypes[index]
                              .financialAccountTypeID &&
                            errors.accountTypes &&
                            errors.accountTypes[index] &&
                            errors.accountTypes[index].financialAccountTypeID
                          }
                          variant="outlined"
                          name={`accountTypes[${index}].financialAccountTypeID`}
                          fullWidth
                          value={type.financialAccountTypeID}
                          onChange={handleChange}
                        >
                          {Object.keys(FINANCIAL_ACCOUNT_TYPE_ID).map(id => (
                            <MenuItem
                              value={FINANCIAL_ACCOUNT_TYPE_ID[id]}
                              key={`financial_account_type_${id}`}
                            >
                              {
                                FINANCIAL_ACCOUNT_TYPE[
                                  FINANCIAL_ACCOUNT_TYPE_ID[id]
                                ]
                              }
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          error={Boolean(
                            errors.accountTypes &&
                              errors.accountTypes[index] &&
                              errors.accountTypes[index].accountno
                          )}
                          helperText={
                            touched.accountTypes &&
                            touched.accountTypes[index] &&
                            touched.accountTypes[index].accountno &&
                            errors.accountTypes &&
                            errors.accountTypes[index] &&
                            errors.accountTypes[index].accountno
                          }
                          variant="outlined"
                          name={`accountTypes[${index}].accountno`}
                          fullWidth
                          value={type.accountno}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          error={Boolean(
                            errors.accountTypes &&
                              errors.accountTypes[index] &&
                              errors.accountTypes[index].balance
                          )}
                          helperText={
                            touched.accountTypes &&
                            touched.accountTypes[index] &&
                            touched.accountTypes[index].balance &&
                            errors.accountTypes &&
                            errors.accountTypes[index] &&
                            errors.accountTypes[index].balance &&
                            "Enter a valid number."
                          }
                          variant="outlined"
                          name={`accountTypes[${index}].balance`}
                          fullWidth
                          value={type.balance}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            )
                          }}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={1} container alignItems="center">
                        <Button
                          simple
                          color="danger"
                          onClick={() => {
                            setFieldValue(
                              "accountTypes",
                              values.accountTypes.filter(
                                (item, i) => index !== i
                              )
                            );
                          }}
                          className={classes.actions}
                        >
                          <Close />
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Box ml={3} mt={2}>
                    <a
                      onClick={() =>
                        setFieldValue("accountTypes", [
                          ...values.accountTypes,
                          {
                            financialAccountTypeID: "",
                            accountno: "",
                            balance: 0
                          }
                        ])
                      }
                    >
                      <strong>Add account type</strong>
                    </a>
                  </Box>
                </Grid>
                <Grid item>
                  <TextField
                    error={Boolean(touched.aba && errors.aba)}
                    helperText={touched.aba && errors.aba}
                    label="ABA Routing Number or Swift Code"
                    variant="outlined"
                    name="aba"
                    fullWidth
                    value={values.aba}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    error={Boolean(touched.signers && errors.signers)}
                    helperText={touched.aba && errors.aba}
                    label="Authorized Signers"
                    variant="outlined"
                    name="signers"
                    fullWidth
                    value={values.signers}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="center" mt={3}>
                {isDirty ? (
                  <Loading />
                ) : (
                  <>
                    <Button color="success" variant="contained" type="submit">
                      Submit
                    </Button>
                    <Box mr={3} />
                    <Button
                      color="danger"
                      variant="contained"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CashDialog;
